<template>
  <div class="attachment-viewer-wrapper">
    <span class="attachment-viewer" @click="handleClick">
      <!-- 缩略图模式 -->
      <template v-if="mode === 'thumbnail'">
        <el-image
            v-if="isImage && previewUrl"
            :src="previewUrl"
            :preview-src-list="[previewUrl]"
            fit="cover"
            class="attachment-viewer__thumb"/>
        <div v-else class="attachment-viewer__thumb attachment-viewer__thumb--fallback">
          <i :class="fileIconClass"/>
        </div>
      </template>
      <!-- 文件名模式（默认） -->
      <template v-else>
        <slot>
          <span class="attachment-viewer__name">{{displayName}}</span>
        </slot>
      </template>
    </span>
    <el-dialog
        :title="dialogTitle"
        :visible.sync="dialogVisible"
        width="80%"
        top="10vh"
        custom-class="attachment-viewer-dialog"
        :close-on-click-modal="false"
        append-to-body
        @close="closeDialog">
      <div class="attachment-viewer__body">
        <!-- 图片预览 -->
        <div
            v-if="isImage && downloadUrl"
            v-loading="imgLoading"
            element-loading-text="图片加载中..."
            class="attachment-viewer__preview">
          <div class="attachment-viewer__toolbar">
            <el-button-group>
              <el-button size="small" title="向左旋转" @click="rotateLeft">
                <i class="el-icon-refresh-left"/>
              </el-button>
              <el-button size="small" title="向右旋转" @click="rotateRight">
                <i class="el-icon-refresh-right"/>
              </el-button>
              <el-button size="small" title="放大" @click="zoomIn">
                <i class="el-icon-zoom-in"/>
              </el-button>
              <el-button size="small" title="缩小" @click="zoomOut">
                <i class="el-icon-zoom-out"/>
              </el-button>
              <el-button size="small" title="重置" @click="resetTransform">
                <i class="el-icon-refresh"/>
              </el-button>
            </el-button-group>
            <span class="attachment-viewer__zoom-info">{{Math.round(imageScale * 100)}}%</span>
          </div>
          <div class="attachment-viewer__image-container">
            <img
                :src="downloadUrl"
                :style="imageTransformStyle"
                class="attachment-viewer__img"
                @load="imgLoading = false"
                @error="imgLoading = false"
                @wheel.prevent="handleWheel">
          </div>
        </div>

        <!-- PDF 预览 -->
        <div
            v-else-if="isPdf && downloadUrl"
            v-loading="pdfLoading"
            :element-loading-text="'PDF 加载中... ' + Math.round(pdfLoadedRatio * 100) + '%'"
            element-loading-spinner="el-icon-loading"
            class="attachment-viewer__pdf">
          <div class="attachment-viewer__controls">
            <el-button type="primary" :disabled="pdfTotalPages > 0 && pdfPage <= 1" @click="prevPage">上一页</el-button>
            <span>第 {{pdfPage}}/{{pdfTotalPages || '-'}} 页</span>
            <el-button type="primary" :disabled="pdfTotalPages > 0 && pdfPage >= pdfTotalPages" @click="nextPage">下一页</el-button>
            <el-button @click="downloadFile">下载</el-button>
          </div>
          <div class="attachment-viewer__pdf-content">
            <pdf
                ref="pdfRef"
                :src="downloadUrl"
                :page="pdfPage"
                @progress="handlePdfProgress"
                @page-loaded="handlePdfPageLoaded"
                @num-pages="handlePdfNumPages"
                @error="handlePdfError"/>
          </div>
        </div>

        <!-- Excel 预览 -->
        <div
            v-else-if="isExcel && downloadUrl"
            v-loading="excelLoading"
            element-loading-text="Excel 加载中..."
            class="attachment-viewer__excel">
          <div v-if="excelSheets.length > 1" class="attachment-viewer__sheet-tabs">
            <el-tabs v-model="currentSheetIndex" @tab-click="handleSheetClick">
              <el-tab-pane
                  v-for="(sheet, index) in excelSheets"
                  :key="sheet"
                  :label="sheet"
                  :name="index"/>
            </el-tabs>
          </div>
          <div class="attachment-viewer__table-wrapper">
            <el-table
                :data="excelTableData"
                border
                stripe
                style="width: 100%"
                height="100%"
                :header-cell-style="{ background: '#f5f7fa', color: '#333' }">
              <el-table-column
                  v-for="(col, colIdx) in excelColumns"
                  :key="colIdx"
                  :label="excelHeaderLabels[colIdx]"
                  :prop="'col_' + colIdx"
                  :min-width="120">
                <template slot-scope="scope">
                  <span>{{scope.row['col_' + colIdx]}}</span>
                </template>
              </el-table-column>
            </el-table>
          </div>
        </div>

        <!-- Word 预览 -->
        <div
            v-else-if="isWord && wordArrayBuffer"
            v-loading="wordLoading"
            element-loading-text="Word 加载中..."
            class="attachment-viewer__office">
          <docx-viewer
              :key="wordArrayBuffer ? wordArrayBuffer.byteLength : 0"
              :src="wordArrayBuffer"
              class="attachment-viewer__office-content"
              @rendered="handleWordRendered"
              @error="handleWordError"/>
        </div>

        <!-- Word 旧格式（doc）不支持预览 -->
        <div
            v-else-if="(isWordLegacy || isPptLegacy) && downloadUrl"
            class="attachment-viewer__other">
          <el-empty :description="fileExtension + ' 格式暂不支持在线预览，请下载后查看'">
            <el-button type="primary" @click="downloadFile">下载文件</el-button>
          </el-empty>
        </div>


        <!-- 其他类型 -->
        <div v-else-if="downloadUrl" class="attachment-viewer__other">
          <el-empty description="该文件类型暂不支持在线预览">
            <el-button type="primary" @click="downloadFile">下载文件</el-button>
          </el-empty>
        </div>

        <!-- 初始加载 -->
        <div v-else-if="loading" class="attachment-viewer__initial">
          <i class="el-icon-loading attachment-viewer__spinner"/>
          <span class="attachment-viewer__loading-text">获取附件中...</span>
        </div>

        <!-- 加载失败 -->
        <div v-else class="attachment-viewer__error">
          <el-empty description="附件加载失败"/>
        </div>
      </div>
    </el-dialog>
  </div>
</template>

<script>
  import pdf from "vue-pdf";
  import * as XLSX from "xlsx";
  import DocxViewer from "@vue-office/docx/lib/v2/vue-office-docx.umd.js";
  import "@vue-office/docx/lib/index.css";

  const IMAGE_EXTENSIONS = ["jpg", "jpeg", "png", "gif", "bmp", "webp"];
  const PDF_EXTENSIONS = ["pdf"];
  const EXCEL_EXTENSIONS = ["xls", "xlsx"];
  const WORD_EXTENSIONS = ["docx"];
  const WORD_LEGACY_EXTENSIONS = ["doc"];
  const PPT_EXTENSIONS = ["pptx"];
  const PPT_LEGACY_EXTENSIONS = ["ppt"];

  export default {
    name: "AttachmentViewer",
    components: { pdf, DocxViewer },
    props: {
      filePath: {
        type: String,
        required: true,
      },
      fileName: {
        type: String,
        default: "",
      },
      mode: {
        type: String,
        default: "name",
        validator: (val) => ["name", "thumbnail"].includes(val),
      },
    },
    data() {
      return {
        dialogVisible: false,
        loading: false,
        downloadUrl: "",
        previewUrl: "",
        pptArrayBuffer: null,
        wordArrayBuffer: null,
        excelSheets: [],
        currentSheetIndex: 0,
        workbookData: null,
        excelData: [],
        excelLoading: false,
        pdfPage: 1,
        pdfTotalPages: 0,
        pdfLoadedRatio: 0,
        pdfLoading: false,
        imgLoading: false,
        wordLoading: false,
        pptLoading: false,
        previewLoadPromise: null,
        imageScale: 1,
        imageRotation: 0,
      };
    },
    computed: {
      dialogTitle() {
        return this.fileName || "附件预览";
      },
      displayName() {
        return this.fileName || "附件预览";
      },
      fileExtension() {
        if (!this.fileName) return "";
        const idx = this.fileName.lastIndexOf(".");
        return idx >= 0 ? this.fileName.substring(idx + 1).toLowerCase() : "";
      },
      fileType() {
        if (IMAGE_EXTENSIONS.includes(this.fileExtension)) return "image";
        if (PDF_EXTENSIONS.includes(this.fileExtension)) return "pdf";
        if (EXCEL_EXTENSIONS.includes(this.fileExtension)) return "excel";
        if (WORD_EXTENSIONS.includes(this.fileExtension)) return "word";
        if (WORD_LEGACY_EXTENSIONS.includes(this.fileExtension)) return "word-legacy";
        if (PPT_EXTENSIONS.includes(this.fileExtension)) return "ppt";
        if (PPT_LEGACY_EXTENSIONS.includes(this.fileExtension)) return "ppt-legacy";
        return "other";
      },
      isImage() {
        return this.fileType === "image";
      },
      isPdf() {
        return this.fileType === "pdf";
      },
      isExcel() {
        return this.fileType === "excel";
      },
      isWord() {
        return this.fileType === "word";
      },
      isWordLegacy() {
        return this.fileType === "word-legacy";
      },
      isPpt() {
        return this.fileType === "ppt";
      },
      isPptLegacy() {
        return this.fileType === "ppt-legacy";
      },
      fileIconClass() {
        if (this.isPdf) return "el-icon-document";
        if (this.isExcel) return "el-icon-s-grid";
        if (this.isWord || this.isWordLegacy) return "el-icon-document";
        if (this.isPpt || this.isPptLegacy) return "el-icon-picture";
        return "el-icon-document";
      },
      excelHeaderLabels() {
        if (this.excelData.length === 0) return [];
        const headerRow = this.excelData[0] || [];
        const maxCols = Math.max(...this.excelData.map(row => row.length));
        return Array.from({ length: maxCols }, (_, i) => {
          const val = headerRow[i];
          return val != null && val !== "" ? String(val) : "列" + (i + 1);
        });
      },
      excelColumns() {
        if (this.excelData.length === 0) return [];
        const maxCols = Math.max(...this.excelData.map(row => row.length));
        return Array.from({ length: maxCols }, (_, i) => i);
      },
      excelTableData() {
        if (this.excelData.length <= 1) return [];
        const dataRows = this.excelData.slice(1);
        return dataRows.map(row => {
          const obj = {};
          for (let i = 0; i < this.excelColumns.length; i++) {
            obj["col_" + i] = row[i] != null ? row[i] : "";
          }
          return obj;
        });
      },
      imageTransformStyle() {
        return {
          transform: `scale(${this.imageScale}) rotate(${this.imageRotation}deg)`,
          transformOrigin: "center center",
          transition: "transform 0.2s ease",
        };
      },
    },
    watch: {
      filePath: {
        immediate: true,
        handler() {
          if (this.mode === "thumbnail" && this.isImage) {
            this.loadPreviewUrl();
          }
        },
      },
    },
    methods: {
      async loadPreviewUrl() {
        if (this.previewLoadPromise) return this.previewLoadPromise;
        this.previewLoadPromise = this.fetchDownloadUrl().then(res => {
          if (res.success) {
            this.previewUrl = res.data;
          }
          this.previewLoadPromise = null;
        }).catch(() => {
          this.previewLoadPromise = null;
        });
        return this.previewLoadPromise;
      },
      async handleClick() {
        this.dialogVisible = true;
        this.loading = true;
        this.resetTransform();
        this.pptArrayBuffer = null;
        this.wordArrayBuffer = null;
        try {
          const res = await this.fetchDownloadUrl();
          if (res.success) {
            this.downloadUrl = res.data;
            if (this.isExcel) {
              this.excelLoading = true;
              await this.parseExcel(res.data);
              this.excelLoading = false;
            } else if (this.isPdf) {
              this.pdfLoading = true;
            } else if (this.isImage) {
              this.imgLoading = true;
            } else if (this.isWord) {
              this.wordLoading = true;
              this.wordArrayBuffer = await this.fetchAsArrayBuffer(res.data);
            }
          }
        } catch (e) {
          console.error("获取附件下载地址失败", e);
          this.$message.error("获取附件下载地址失败");
        } finally {
          this.loading = false;
        }
      },
      async fetchAsArrayBuffer(url) {
        const response = await fetch(url);
        if (!response.ok) throw new Error("Failed to fetch file");
        return await response.arrayBuffer();
      },
      handleWordRendered() {
        this.wordLoading = false;
      },
      handleWordError(err) {
        console.error("Word 渲染失败", err);
        this.wordLoading = false;
        this.$message.error("Word 文件渲染失败");
      },
      fetchDownloadUrl() {
        return this.$http.get("basic", `files/getDownloadUrl?key=${this.filePath}`);
      },
      async parseExcel(url) {
        try {
          const response = await fetch(url);
          const arrayBuffer = await response.arrayBuffer();
          this.workbookData = XLSX.read(arrayBuffer, { type: "array" });
          this.excelSheets = this.workbookData.SheetNames;
          this.currentSheetIndex = 0;
          this.excelData = this.getSheetData(this.workbookData, 0);
        } catch (e) {
          console.error("Excel 解析失败", e);
          this.$message.error("Excel 文件解析失败");
          this.excelData = [];
        }
      },
      getSheetData(workbook, sheetIndex) {
        const sheetName = workbook.SheetNames[sheetIndex];
        const sheet = workbook.Sheets[sheetName];
        return XLSX.utils.sheet_to_json(sheet, { header: 1, defval: "" });
      },
      handleSheetClick(tab) {
        const index = tab.index;
        this.currentSheetIndex = index;
        if (this.workbookData) {
          this.excelData = this.getSheetData(this.workbookData, index);
        }
      },
      closeDialog() {
        this.dialogVisible = false;
        this.downloadUrl = "";
        this.pptArrayBuffer = null;
        this.wordArrayBuffer = null;
        this.imgLoading = false;
        this.pdfLoading = false;
        this.excelLoading = false;
        this.wordLoading = false;
        this.pptLoading = false;
        this.excelData = [];
        this.excelSheets = [];
        this.workbookData = null;
        this.pdfPage = 1;
        this.pdfTotalPages = 0;
        this.pdfLoadedRatio = 0;
        this.resetTransform();
      },
      rotateLeft() {
        this.imageRotation -= 90;
      },
      rotateRight() {
        this.imageRotation += 90;
      },
      zoomIn() {
        this.imageScale = Math.min(this.imageScale + 0.1, 5);
      },
      zoomOut() {
        this.imageScale = Math.max(this.imageScale - 0.1, 0.2);
      },
      resetTransform() {
        this.imageScale = 1;
        this.imageRotation = 0;
      },
      handleWheel(e) {
        if (e.deltaY < 0) {
          this.zoomIn();
        } else {
          this.zoomOut();
        }
      },
      prevPage() {
        this.pdfPage = Math.max(1, this.pdfPage - 1);
      },
      nextPage() {
        this.pdfPage = Math.min(this.pdfTotalPages, this.pdfPage + 1);
      },
      handlePdfProgress(ratio) {
        this.pdfLoadedRatio = ratio;
      },
      handlePdfPageLoaded() {
        this.pdfLoading = false;
      },
      handlePdfNumPages(total) {
        this.pdfTotalPages = total;
      },
      handlePdfError(err) {
        console.error("PDF 加载失败", err);
        this.pdfLoading = false;
      },
      downloadFile() {
        if (this.downloadUrl) {
          window.open(this.downloadUrl);
        }
      },
    },
  };
</script>

<style scoped lang="scss">
.attachment-viewer-wrapper {
  display: inline;
}

.attachment-viewer {
  color: #2d8cf0;
  cursor: pointer;
  text-decoration: none;
  display: inline-block;

  &:hover {
    text-decoration: underline;
  }

  &__name {
    display: inline-block;
  }

  &__thumb {
    width: 40px;
    height: 40px;
    display: inline-block;
    border-radius: 4px;
    vertical-align: middle;

    &--fallback {
      background: #f5f7fa;
      display: flex;
      align-items: center;
      justify-content: center;

      i {
        font-size: 20px;
        color: #909399;
      }
    }
  }

  &__body {
    height: 80vh;
    overflow-y: auto;
    padding: 0 20px;
  }

  &__initial {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    height: 100%;
    gap: 16px;
  }

  &__spinner {
    font-size: 36px;
    color: #409eff;
  }

  &__loading-text {
    color: #606266;
    font-size: 14px;
  }

  &__preview {
    display: flex;
    flex-direction: column;
    height: 100%;
  }

  &__toolbar {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 16px;
    padding: 12px 0;
    border-bottom: 1px solid #ebeef5;
    margin-bottom: 12px;
    flex-shrink: 0;
  }

  &__zoom-info {
    color: #606266;
    font-size: 13px;
    min-width: 50px;
    text-align: right;
  }

  &__image-container {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: auto;
    background: #f5f7fa;
    border-radius: 4px;
    padding: 20px;
    min-height: 400px;
  }

  &__img {
    max-width: 100%;
    max-height: 100%;
    cursor: grab;
    user-select: none;
    -webkit-user-drag: none;
  }

  &__controls {
    display: flex;
    gap: 16px;
    justify-content: center;
    align-items: center;
    margin-bottom: 16px;
    flex-shrink: 0;
  }

  &__pdf {
    display: flex;
    flex-direction: column;
    height: 100%;
  }

  &__pdf-content {
    flex: 1;
    overflow: auto;
    text-align: center;
    min-height: 400px;
  }

  &__excel {
    display: flex;
    flex-direction: column;
    height: 100%;
  }

  &__sheet-tabs {
    margin-bottom: 12px;
    flex-shrink: 0;
  }

  &__table-wrapper {
    flex: 1;
    overflow: auto;
  }

  &__office {
    display: flex;
    flex-direction: column;
    height: 100%;
    background: #fff;

    &-content {
      flex: 1;
      overflow: auto;
      background: #fff;
      min-height: 400px;
    }

    ::v-deep .docx-wrapper {
      padding: 0 !important;
      height: 100% !important;
      overflow: auto;
      background: #fff;
    }

    ::v-deep .docx {
      padding: 20px !important;
      box-shadow: none !important;
    }

    ::v-deep .vue-office-pptx-main {
      width: 100% !important;
      height: 100% !important;
    }

    ::v-deep .pptx-preview-wrapper {
      background: #fff !important;
    }

    ::v-deep .pptx-preview-slide-wrapper {
      background: #fff !important;
    }

    ::v-deep .pptx-preview-wrapper canvas {
      display: block;
      background: #fff !important;
    }
  }

  &__other,
  &__error {
    padding: 40px;
    text-align: center;
  }
}
</style>

<style lang="scss">
.attachment-viewer-dialog {
  .el-dialog__body {
    height: 80vh;
    overflow: hidden;
    padding: 10px 0;
    display: flex;
    flex-direction: column;
  }
}
</style>
